import type { SocialConnection, SocialConnectionPlatform, SocialPost } from '@branditdev/mf-types'
import branditRails from '../../../../api/brandit_rails'

export type SocialPlatformType = 'FACEBOOK' | 'INSTAGRAM'

export const getSocialConnection = async (locationId: number | string) => {
  const { data } = await branditRails.get<SocialConnection>('/social_connection', {
    params: { location_id: locationId }
  })
  return data
}

export const connectSocialAccount = async (
  locationId: number | string,
  type: SocialPlatformType,
  redirectUrl: string
) => {
  const { data } = await branditRails.post<{ oauth_url: string }>(
    '/social_connection/connect',
    { type, redirect_url: redirectUrl },
    { params: { location_id: locationId } }
  )
  return data
}

export const disconnectSocialAccount = async (locationId: number | string, type: SocialPlatformType) => {
  const { data } = await branditRails.delete<SocialConnection>('/social_connection/disconnect', {
    params: { location_id: locationId, type }
  })
  return data
}

export const syncSocialConnection = async (locationId: number | string) => {
  const { data } = await branditRails.post<SocialConnection>(
    '/social_connection/sync',
    {},
    { params: { location_id: locationId } }
  )
  return data
}

export const updateSocialConnectionPlatformNickname = async (
  locationId: number | string,
  platformKey: string,
  nickname: string
) => {
  const { data } = await branditRails.patch<SocialConnection>(
    `/social_connection/platforms/${platformKey}`,
    { nickname },
    { params: { location_id: locationId } }
  )
  return data
}

export const publishSocialPost = async (
  locationId: number | string,
  payload: {
    product_id: number | string
    digital_proofing_product_id: number | string
    platforms: SocialPlatformType[]
    caption: string
    channel_ids?: Partial<Record<'facebook' | 'instagram', string>>
  }
) => {
  const { data } = await branditRails.post<SocialPost>(`/locations/${locationId}/social_posts`, payload)
  return data
}

export type SocialPostsIndexResponse = {
  social_posts: SocialPost[]
  meta: {
    total: number
  }
}

export const fetchSocialPosts = async (locationId: number | string): Promise<SocialPostsIndexResponse> => {
  const { data } = await branditRails.get<SocialPostsIndexResponse>(`/locations/${locationId}/social_posts`)
  return data
}

export const isPlatformConnected = (connection: SocialConnection | null | undefined, platformKey: string): boolean => {
  return (connection?.social_connection_platforms || []).some(
    (row: SocialConnectionPlatform) => row.platform === platformKey && row.status === 'connected'
  )
}

export const platformRow = (
  connection: SocialConnection | null | undefined,
  platformKey: string
): SocialConnectionPlatform | undefined => {
  return (connection?.social_connection_platforms || []).find(
    (row: SocialConnectionPlatform) => row.platform === platformKey
  )
}

export type SocialChannelOption = {
  id: string
  name?: string | null
}

export const platformChannels = (
  connection: SocialConnection | null | undefined,
  platformKey: string
): SocialChannelOption[] => {
  const channels = platformRow(connection, platformKey)?.channels
  if (!Array.isArray(channels)) return []

  return channels.flatMap(channel => {
    if (!channel || typeof channel !== 'object') return []
    const id = 'id' in channel ? String(channel.id ?? '') : ''
    if (!id) return []
    const name = 'name' in channel && channel.name != null ? String(channel.name) : id
    return [{ id, name }]
  })
}
